import { useState } from "react";
import { motion } from "framer-motion";
import ProjectModal from "../components/ProjectModal";

const projects = [
  {
    title: "H&M MENA eCommerce",
    description:
      "Cross-platform eCommerce mobile app built in React Native for H&M Middle East and North Africa — clothing, swimwear, accessories and shoes.",
    link: "https://play.google.com/store/apps/details?id=com.hm.mena",
    tags: ["React Native", "eCommerce", "iOS", "Android"],
    image: "./assets/h&m.png",
    status: "COMPLETED",
    rarity: "LEGENDARY",
  },
  {
    title: "Victoria's Secret MENA",
    description:
      "Full-featured shopping app with exclusive offers, styling content and size-inclusive catalogue across lingerie, sport, swim and beauty.",
    link: "https://play.google.com/store/apps/details?id=com.vs.mena",
    tags: ["React Native", "Fashion", "iOS", "Android"],
    status: "COMPLETED",
    rarity: "LEGENDARY",
  },
  {
    title: "Arby's Fast Food (InspireBrands)",
    description:
      "Online ordering mobile app for Arby's restaurant chain, part of the InspireBrands portfolio.",
    link: "https://apps.apple.com/us/app/arbys-fast-food-sandwiches/id1348507359",
    tags: ["React Native", "Food", "iOS"],
    status: "COMPLETED",
    rarity: "EPIC",
  },
  {
    title: "Buffalo Wild Wings (InspireBrands)",
    description:
      "Rewards, online ordering, and free-to-play sports gaming integrated into a single React Native app.",
    link: "https://play.google.com/store/apps/details?id=com.buffalowildwings.blazinrewards",
    tags: ["React Native", "Food", "Rewards"],
    status: "COMPLETED",
    rarity: "EPIC",
  },
  {
    title: "Sentara Healthcare",
    description:
      "Angular web application for a not-for-profit healthcare organisation serving Virginia and northeastern North Carolina.",
    link: "https://www.sentara.com/",
    tags: ["Angular", "Healthcare", "Web"],
    status: "COMPLETED",
    rarity: "RARE",
  },
  {
    title: "Boots",
    description:
      "Cross-platform eCommerce experience for one of the UK's leading pharmacy and beauty retailers, built with React Native.",
    link: "https://apps.apple.com/gb/app/boots/id880993267",
    tags: ["React Native", "eCommerce", "iOS"],
    status: "COMPLETED",
    rarity: "EPIC",
  },
  {
    title: "Syngenta Smart Engage",
    description:
      "B2B mobile platform for global sales reps with grower maps, contact and lead management, event management, Qlik integration and counterfeit detection.",
    link: "https://apps.apple.com/br/app/smart-engage-syngenta/id6443504370",
    tags: ["React Native", "B2B", "Maps", "iOS", "Android"],
    status: "COMPLETED",
    rarity: "EPIC",
  },
];

const tagColorMap = {
  "React Native": "bg-cyan-500/10 border-cyan-500/25 text-cyan-300",
  eCommerce: "bg-purple-500/10 border-purple-500/25 text-purple-300",
  Angular: "bg-red-500/10 border-red-500/25 text-red-300",
  iOS: "bg-blue-500/10 border-blue-500/25 text-blue-300",
  Android: "bg-green-500/10 border-green-500/25 text-green-300",
  Web: "bg-yellow-500/10 border-yellow-500/25 text-yellow-300",
  B2B: "bg-orange-500/10 border-orange-500/25 text-orange-300",
};

const defaultTag = "border-cyan-500/20 text-slate-300/80";

const rarityStyles = {
  LEGENDARY: {
    badge:
      "bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-400/40 text-amber-200",
    glow: "shadow-[0_0_12px_rgba(251,191,36,0.15)]",
  },
  EPIC: {
    badge:
      "bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-400/40 text-purple-200",
    glow: "shadow-[0_0_12px_rgba(168,85,247,0.15)]",
  },
  RARE: {
    badge:
      "bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-400/40 text-blue-200",
    glow: "shadow-[0_0_12px_rgba(59,130,246,0.15)]",
  },
};

const statusStyles = {
  COMPLETED: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
  ACTIVE: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300",
  "IN DEV": "bg-yellow-500/10 border-yellow-500/30 text-yellow-300",
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: "easeOut" },
  }),
};

const HackerProjects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const openModal = (project) => {
    setSelectedProject(project);
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
    setSelectedProject(null);
  };

  return (
    <div className="py-2 font-mono">
      <div className="flex items-center gap-3 mb-8">
        <h2 className="text-sm tracking-[0.15em] text-slate-200/90 font-mono">
          <span className="text-cyan-400/60">$</span> ls ~/projects/completed
        </h2>
        <div
          className="flex-1 h-px"
          style={{
            background:
              "linear-gradient(90deg, rgba(6,182,212,0.3), transparent)",
          }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
        {projects.map((project, i) => (
          <motion.div
            key={i}
            custom={i}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            onClick={() => openModal(project)}
            className="project-card p-5 cursor-pointer"
          >
            <div
              className="absolute top-0 left-0 right-0 h-px rounded-t-xl"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(0,255,153,0.4), transparent)",
              }}
            />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-slate-500/70 font-mono">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex gap-1.5">
                  {project.rarity && (
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full border font-bold tracking-wider ${rarityStyles[project.rarity]?.badge} ${rarityStyles[project.rarity]?.glow}`}
                    >
                      {project.rarity}
                    </span>
                  )}
                  {project.status && (
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded border font-mono ${statusStyles[project.status]}`}
                    >
                      {project.status}
                    </span>
                  )}
                </div>
              </div>
              <h3 className="text-base font-bold text-slate-100 mb-2 leading-snug">
                {project.title}
              </h3>
              <p className="text-xs text-slate-300/80 leading-relaxed mb-4 line-clamp-3">
                {project.description}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {project.tags?.map((tag) => (
                  <span
                    key={tag}
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs border font-mono ${
                      tagColorMap[tag] || defaultTag
                    }`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs text-cyan-300/70">
                <span>View details</span>
                <span className="ml-1">&rarr;</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <ProjectModal
        isOpen={isOpen}
        closeModal={closeModal}
        project={selectedProject}
      />
    </div>
  );
};

export default HackerProjects;
